import { Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { enabledLanguages } from '../../../../../Intl/setup';
import { setLocale } from '../../../../AppActions';
import { getI18n } from '../../../../AppReducer';
import { updateUserLocaleAction } from '../../../Auth/AuthActions';

const footerSx = {
  flex: '0 0 auto',
  textAlign: 'center',
  py: 7,
  px: 1,
  background: '#1f2225',
  color: '#7a7d84',
  '& p': {
    m: 0,
    mb: 1,
    fontSize: 14,
    color: '#7a7d84',
  },
  '& a': {
    color: '#ccc',
    textDecoration: 'none',
    fontWeight: 700,
    '&:visited': { color: '#ccc' },
    '&:hover': { color: '#fff' },
  },
};

const footerColumnsSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
  gap: 3,
  justifyContent: 'center',
  mb: 4,
};

const footerColumnSx = {
  px: { xs: 2, md: 0 },
  maxWidth: { xs: 480, md: 'none' },
  mx: { xs: 'auto', md: 0 },
};

const columnHeadingSx = {
  textAlign: 'left',
  fontSize: '1.2rem',
  color: '#eee',
  m: 0,
  mb: 1,
  fontWeight: 600,
};

const footerLinksSx = {
  mt: 1.25,
  pl: 0,
  textAlign: 'left',
  listStyle: 'none',
  '& li': { mb: 0.5 },
  '& a': { fontWeight: 200 },
};

const languageSwitcherSx = {
  textAlign: 'center',
  color: '#ccc',
  mt: 4,
  '& ul': {
    listStyle: 'none',
    m: 'auto',
    textAlign: 'center',
    display: 'inline-block',
    pl: 2,
  },
  '& li': {
    display: 'inline-block',
    m: '10px',
    p: '5px',
    cursor: 'pointer',
    '& a': { color: '#2196F3', fontWeight: 700 },
  },
};

function Footer(props) {
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = ['/en', '/es', '/', ''].indexOf(location.pathname) >= 0;

  const switchLanguage = async (event, newLang) => {
    event.preventDefault();

    props.setLocale(newLang);

    try {
      await props.updateUserLocaleAction(newLang);
    } catch (error) {
      console.warn('Failed to update user locale preference:', error);
    }

    if (isHomePage) {
      navigate(`/${newLang}`);
    }
  };

  const languageNodes = enabledLanguages.map(lang => (
    <Box
      component="li"
      key={lang}
      sx={lang === props.locale ? { borderBottom: '1px solid #ccc' } : undefined}
    >
      <a href={`${isHomePage ? '/' : '#'}${lang}`} onClick={event => switchLanguage(event, lang)}>
        {lang}
      </a>
    </Box>
  ));

  return (
    <Box component="footer" sx={footerSx}>
      <Box sx={{ width: '100%', maxWidth: 1280, margin: 'auto', px: 1 }}>
        <Box sx={footerColumnsSx}>
          <Box sx={footerColumnSx}>
            <Typography component="h3" sx={columnHeadingSx}>
              <FormattedMessage id="Footer.Col1" defaultMessage="Integrations and doubts" />
            </Typography>
            <Box component="ul" sx={footerLinksSx}>
              <li>
                <a href="/apidocs">API</a>
              </li>
              <li>
                <Link to="/help">
                  <FormattedMessage id="Footer.Help" defaultMessage="Help" />
                </Link>
              </li>
              <li>
                <a href="mailto:hello@promoter.ninja">
                  <FormattedMessage id="Footer.Contact" defaultMessage="Contact Us" />
                </a>
              </li>
            </Box>
          </Box>
          <Box sx={footerColumnSx}>
            <Typography component="h3" sx={columnHeadingSx}>
              <FormattedMessage id="Footer.Col2" defaultMessage="Legal" />
            </Typography>
            <Box component="ul" sx={footerLinksSx}>
              <li>
                <Link to="/legal/privacy">
                  <FormattedMessage id="Footer.Privacy" defaultMessage="Privacy Policy" />
                </Link>
              </li>
              <li>
                <Link to="/legal/terms">
                  <FormattedMessage id="Footer.Terms" defaultMessage="Terms of Use" />
                </Link>
              </li>
              <li>
                <Link to="/legal/cookies">
                  <FormattedMessage id="Footer.Cookies" defaultMessage="Cookie Policy" />
                </Link>
              </li>
            </Box>
          </Box>
          {props.locale === 'es' ? (
            <Box sx={footerColumnSx}>
              <Typography component="h3" sx={columnHeadingSx}>
                <FormattedMessage id="Footer.Col3" defaultMessage="Attributes" />
              </Typography>
              <Box component="ul" sx={footerLinksSx}>
                <li>
                  <a href="calculadora-net-promoter-score.html">Calculadora Net Promoter Score</a>
                </li>
                <li>
                  <a href="net-promoter-score.html">Net Promoter Score - NPS</a>
                </li>
                <li>
                  <a href="encuesta-nps-gratis.html">Encuesta NPS gratis</a>
                </li>
                <li>
                  <a href="encuesta-satisfaccion-clientes.html">
                    Encuesta de Satisfacción de Clientes
                  </a>
                </li>
              </Box>
            </Box>
          ) : null}
        </Box>
        <Box sx={languageSwitcherSx}>
          <FormattedMessage id="switchLanguage" defaultMessage="Switch language" />
          <ul>{languageNodes}</ul>
        </Box>
        <Box sx={{ mt: 4 }}>
          <p>
            &copy;
            {new Date().getFullYear()} &middot; Promoter Ninja
          </p>
          <FormattedMessage
            id="Footer.NPS"
            defaultMessage="Net Promoter, NPS are registered service marks, and Net Promoter Score and
          Net Promoter System are service marks, of Bain & Company, Inc., Satmetrix Systems, Inc. and Fred Reichheld."
          >
            {msg => <p>{msg}</p>}
          </FormattedMessage>
        </Box>
      </Box>
    </Box>
  );
}

const mapStateToProps = state => ({
  locale: getI18n(state).locale,
});

const mapDispatchToProps = dispatch => ({
  setLocale(locale) {
    dispatch(setLocale(locale));
  },
  updateUserLocaleAction(locale) {
    return dispatch(updateUserLocaleAction(locale));
  },
});

Footer.propTypes = {
  locale: PropTypes.string.isRequired,
  setLocale: PropTypes.func.isRequired,
  updateUserLocaleAction: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(Footer);
